// Google Analytics 4 Configuration and Event Tracking
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Initialize gtag if not already available
export const initGA = () => {
  if (typeof window !== 'undefined' && !window.gtag) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
    
    // Set default consent state (denied until user chooses)
    window.gtag('consent', 'default', {
      'analytics_storage': 'denied',
      'ad_storage': 'denied',
      'functionality_storage': 'denied',
      'personalization_storage': 'denied',
      'security_storage': 'granted'
    });
  }
};
import { 
  clarityTrackPageView,
  clarityTrackLanguageChange,
  clarityTrackFormSubmission,
  clarityTrackCTAClick,
  clarityTrackServiceInteraction,
  clarityTrackHeroInteraction,
  clarityTrackTireConfigurator,
  clarityTrackFAQInteraction,
  clarityTrackGalleryInteraction,
  clarityTrackBrandClick,
  clarityTrackQuoteRequest,
  clarityTrackAdminAccess,
  clarityTrackError,
  clarityTrackPerformance,
  clarityTrackScrollDepth
} from './clarity';

// Page view tracking
export const trackPageView = (path: string, title?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-7JFK1XQ04L', {
      page_path: path,
      page_title: title || document.title,
      page_location: window.location.href
    });
    
    // Custom page view event
    window.gtag('event', 'page_view', {
      page_title: title || document.title,
      page_location: window.location.href,
      page_path: path,
      language: document.documentElement.lang || 'fr'
    });
  }
  
  // Also track with Clarity
  clarityTrackPageView(path, title);
};

// Language change tracking
export const trackLanguageChange = (fromLang: string, toLang: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'language_change', {
      event_category: 'User Interaction',
      event_label: `${fromLang}_to_${toLang}`,
      from_language: fromLang,
      to_language: toLang,
      custom_parameter_1: 'language_switch'
    });
  }
  
  // Also track with Clarity
  clarityTrackLanguageChange(fromLang, toLang);
};

// Contact form tracking
export const trackFormSubmission = (formName: string, formData?: any) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'form_submit', {
      event_category: 'Form Interaction',
      event_label: formName,
      form_name: formName,
      form_location: window.location.pathname,
      language: document.documentElement.lang || 'fr',
      ...formData
    });
  }
  
  // Also track with Clarity
  clarityTrackFormSubmission(formName, formData);
};

// CTA button tracking
export const trackCTAClick = (ctaName: string, location: string, destination?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'cta_click', {
      event_category: 'CTA Interaction',
      event_label: ctaName,
      cta_name: ctaName,
      cta_location: location,
      destination_url: destination,
      page_path: window.location.pathname,
      language: document.documentElement.lang || 'fr'
    });
  }
  
  // Also track with Clarity
  clarityTrackCTAClick(ctaName, location, destination);
};

// Service card interaction tracking
export const trackServiceInteraction = (serviceName: string, action: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'service_interaction', {
      event_category: 'Service Engagement',
      event_label: `${serviceName}_${action}`,
      service_name: serviceName,
      interaction_type: action,
      page_path: window.location.pathname,
      language: document.documentElement.lang || 'fr'
    });
  }
  
  // Also track with Clarity
  clarityTrackServiceInteraction(serviceName, action);
};

// Hero carousel tracking
export const trackHeroInteraction = (slideIndex: number, slideType: string, action: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'hero_interaction', {
      event_category: 'Hero Carousel',
      event_label: `${slideType}_${action}`,
      slide_index: slideIndex,
      slide_type: slideType,
      interaction_type: action,
      language: document.documentElement.lang || 'fr'
    });
  }
  
  // Also track with Clarity
  clarityTrackHeroInteraction(slideIndex, slideType, action);
};

// Gallery interaction tracking
export const trackGalleryInteraction = (imageId: string, action: string, category?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'gallery_interaction', {
      event_category: 'Gallery Engagement',
      event_label: `${category || 'unknown'}_${action}`,
      image_id: imageId,
      interaction_type: action,
      image_category: category,
      language: document.documentElement.lang || 'fr'
    });
  }
  
  // Also track with Clarity
  clarityTrackGalleryInteraction(imageId, action, category);
};

// Tire configurator tracking
export const trackTireConfiguratorStep = (step: string, value: any, isComplete: boolean = false) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'tire_configurator', {
      event_category: 'Tire Configuration',
      event_label: `${step}_${value}`,
      configurator_step: step,
      selected_value: value,
      is_configuration_complete: isComplete,
      language: document.documentElement.lang || 'fr'
    });
  }
  
  // Also track with Clarity
  clarityTrackTireConfigurator(step, value, isComplete);
};

// FAQ interaction tracking
export const trackFAQInteraction = (questionId: string, question: string, category: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'faq_interaction', {
      event_category: 'FAQ Engagement',
      event_label: `${category}_${questionId}`,
      question_id: questionId,
      question_category: category,
      question_text: question.substring(0, 100), // Limit text length
      language: document.documentElement.lang || 'fr'
    });
  }
  
  // Also track with Clarity
  clarityTrackFAQInteraction(questionId, question, category);
};

// Brand logo click tracking
export const trackBrandClick = (brandName: string, location: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'brand_click', {
      event_category: 'Brand Interaction',
      event_label: `${brandName}_${location}`,
      brand_name: brandName,
      click_location: location,
      language: document.documentElement.lang || 'fr'
    });
  }
  
  // Also track with Clarity
  clarityTrackBrandClick(brandName, location);
};

// Custom quote request tracking
export const trackQuoteRequest = (projectType: string, formData: any) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'quote_request', {
      event_category: 'Lead Generation',
      event_label: projectType,
      project_type: projectType,
      has_vehicle_info: !!(formData.vehicleBrand && formData.vehicleModel),
      estimated_budget: formData.budget,
      language: document.documentElement.lang || 'fr',
      value: getBudgetValue(formData.budget) // Convert budget to numeric value for GA
    });
  }
  
  // Also track with Clarity
  clarityTrackQuoteRequest(projectType, formData);
};

// Admin access tracking
export const trackAdminAccess = (action: string, userId?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'admin_access', {
      event_category: 'Admin Panel',
      event_label: action,
      admin_action: action,
      user_id: userId || 'anonymous',
      timestamp: new Date().toISOString()
    });
  }
  
  // Also track with Clarity
  clarityTrackAdminAccess(action, userId);
};

// Error tracking
export const trackError = (error: Error, component: string, additionalData?: any) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'exception', {
      description: `${component}: ${error.message}`,
      fatal: false,
      component_name: component,
      error_message: error.message,
      error_stack: error.stack?.substring(0, 200), // Limit stack trace
      ...additionalData
    });
  }
  
  // Also track with Clarity
  clarityTrackError(error, component, additionalData);
};

// Performance tracking
export const trackPerformance = (metric: string, value: number, unit: string = 'ms') => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'performance_metric', {
      event_category: 'Performance',
      event_label: metric,
      metric_name: metric,
      metric_value: value,
      metric_unit: unit,
      page_path: window.location.pathname
    });
  }
  
  // Also track with Clarity
  clarityTrackPerformance(metric, value, unit);
};

// Scroll depth tracking
export const trackScrollDepth = (percentage: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'scroll', {
      event_category: 'User Engagement',
      event_label: `${percentage}%`,
      scroll_depth: percentage,
      page_path: window.location.pathname,
      language: document.documentElement.lang || 'fr'
    });
  }
  
  // Also track with Clarity
  clarityTrackScrollDepth(percentage);
};

// Utility function to convert budget string to numeric value
const getBudgetValue = (budget: string): number => {
  const budgetMap: { [key: string]: number } = {
    '1000-2500': 1750,
    '2500-5000': 3750,
    '5000-10000': 7500,
    '10000-20000': 15000,
    '20000+': 25000,
    'to-discuss': 0
  };
  return budgetMap[budget] || 0;
};

// Initialize Analytics
export const setupAnalytics = () => {
  initGA();
  
  // Track initial page load
  if (typeof window !== 'undefined') {
    trackPageView(window.location.pathname, document.title);
    
    // Set up scroll tracking
    let scrollTracked = { 25: false, 50: false, 75: false, 90: false };
    
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);
      
      Object.keys(scrollTracked).forEach(threshold => {
        const thresholdNum = parseInt(threshold);
        if (scrollPercent >= thresholdNum && !scrollTracked[thresholdNum]) {
          scrollTracked[thresholdNum] = true;
          trackScrollDepth(thresholdNum);
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Track performance metrics
    window.addEventListener('load', () => {
      if ('performance' in window) {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (navigation) {
          trackPerformance('page_load_time', navigation.loadEventEnd - navigation.fetchStart);
          trackPerformance('dom_content_loaded', navigation.domContentLoadedEventEnd - navigation.fetchStart);
          trackPerformance('first_paint', navigation.responseEnd - navigation.fetchStart);
        }
      }
    });
    
    // Track unhandled errors
    window.addEventListener('error', (event) => {
      trackError(event.error, 'Global Error Handler', {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      });
    });
    
    // Track unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      trackError(new Error(event.reason), 'Unhandled Promise Rejection');
    });
  }
};